import React, { useState, useCallback, useEffect } from "react";
import { Upload, FileText } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../services/api/axios";
import { useUser } from "../../../context/UserContext";
import { getUserById } from "../../../services/users";
import {
  CloseButton,
  GradientButton,
  PrimaryButton,
} from "../../../components/Buttons";
import usePrivilege from "../../../hooks/usePrivilege";
const AgreementsTab = ({ sale, onSaleUpdate, isHandover, isCancelled }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [aadharNo, setAadharNo] = useState("");
  const [panNo, setPanNo] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFinalAgreementRadio, setIsFinalAgreementRadio] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [secondaryUsers, setSecondaryUsers] = useState([]);
  const user = useUser();
  const [aadharPanData, setAadharPanData] = useState({});
  const [agreementFiles, setAgreementFiles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [fetchingAgreements, setFetchingAgreements] = useState(false);
  const { hasPrivilege } = usePrivilege();

  const openModal = async () => {
    if (sale?.secondaryUsersId && sale.secondaryUsersId.length > 0) {
      await fetchSecondaryUsers();
    } else {
      setSecondaryUsers([]);
      setAadharPanData({});
    }
    setModalIsOpen(true);
  };
  // Stable identity so it doesn't churn the handleGenerateDraft useCallback deps.
  const closeModal = useCallback(() => setModalIsOpen(false), []);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleFinalAgreementRadioChange = (e) => {
    setIsFinalAgreementRadio(e.target.value === "yes");
  };

  const fetchAgreements = async () => {
    // Fetch the agreements UPLOADED for THIS sale — not the project-level
    // agreement template (/projects/media?agreement=true), which is what was
    // previously (incorrectly) queried, so uploaded agreements never showed.
    if (!sale?.customerSalesId) {
      return;
    }

    setFetchingAgreements(true);
    try {
      // The endpoint is split by isFinal: false = draft, true = final/uploaded.
      // Fetch BOTH so a final upload (isFinal=true) shows alongside the draft
      // — previously only the default (draft) was queried, so finals never
      // appeared.
      const [finalResp, draftResp] = await Promise.all([
        axiosInstance.get("/customer/sales/agreement", {
          params: { customer_sales_id: sale.customerSalesId, isFinal: true },
        }),
        axiosInstance.get("/customer/sales/agreement", {
          params: { customer_sales_id: sale.customerSalesId, isFinal: false },
        }),
      ]);
      const toFiles = (resp, kind) => {
        const items = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        // Backend returns { contentType, url, key } per agreement.
        return items.map((item, index) => {
          const key = item.key || item.s3Key || "";
          return {
            name: key.split("/").pop() || `${kind} Agreement ${index + 1}`,
            url: item.url || item.downloadUrl || "",
            kind,
          };
        });
      };
      // Final first, then draft.
      setAgreementFiles([...toFiles(finalResp, "Final"), ...toFiles(draftResp, "Draft")]);
    } catch (error) {
      console.error("Failed to fetch agreement files:", error);
      setAgreementFiles([]);
    } finally {
      setFetchingAgreements(false);
    }
  };

  const fetchSecondaryUsers = async () => {
    if (sale?.secondaryUsersId && sale.secondaryUsersId.length > 0) {
      try {
        const userPromises = sale.secondaryUsersId.map(async (userId) => {
          const response = await getUserById(userId);
          return response;
        });
        const userDetails = await Promise.all(userPromises);
        setSecondaryUsers(userDetails);
        const initialAadharPanData = {};
        userDetails.forEach((user) => {
          initialAadharPanData[user.userId] = { aadharNo: "", panNo: "" };
        });
        setAadharPanData(initialAadharPanData);
      } catch (error) {
        console.error("Failed to fetch secondary users:", error);
        toast.error("Failed to fetch secondary users");
      }
    } else {
      setSecondaryUsers([]);
      setAadharPanData({});
    }
  };

  const handleInputChange = (userId, field, value) => {
    setAadharPanData((prevData) => ({
      ...prevData,
      [userId]: {
        ...prevData[userId],
        [field]: value,
      },
    }));
  };

  const handleGenerateDraft = useCallback(async () => {
    setGenerating(true);
    try {
      const secondaryUsersDetail = secondaryUsers.map((userArray) => {
        const user = userArray[0];
        return {
          user: {
            firstName: user.firstName,
            lastName: user.lastName,
            mobileNoWa: user.mobileNoWa,
            mobileNoCalling: user.mobileNoCalling,
            emailId: user.emailId,
            address: user.address,
            gender: user.gender,
            title: user.title,
            dob: user.dob,
          },
          aadharNo: aadharPanData[user.userId]?.aadharNo || "",
          panNo: aadharPanData[user.userId]?.panNo || "",
        };
      });

      const payload = {
        primaryUserDetail: {
          user: {
            firstName: sale?.user?.firstName,
            lastName: sale?.user?.lastName,
            mobileNoWa: sale?.user?.mobileNoWa,
            mobileNoCalling: sale?.user?.mobileNoCalling,
            emailId: sale?.user?.emailId,
            address: sale?.user?.address,
            gender: sale?.user?.gender,
            title: sale?.user?.title,
            dob: sale?.user?.dob,
          },
          aadharNo: aadharNo,
          panNo: panNo,
        },
        secondaryUsersDetail: secondaryUsersDetail,
      };

      const response = await axiosInstance.post(
        `/customer/sales/draft-agreement?customerSalesId=${sale.customerSalesId}`,
        payload,
        { responseType: "blob" } // Setting response type to blob
      );

      // Create a blob from the response
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      }); // Making sure we set the mime type correctly
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Agreement_${sale.customerSalesId}.docx`);
      document.body.appendChild(link);

      // Programmatically click the link to start the download
      link.click();

      // Clean up: remove the link and revoke the blob URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Agreement generated successfully");
      closeModal();
    } catch (error) {
      console.error("Error generating draft:", error);
      if (error.response && error.response.data) {
        const message =
          error.response.data.message ||
          "Failed to generate the draft agreement";
        toast.error(message);
      } else {
        toast.error(error.message || "Failed to generate the draft agreement");
      }
    } finally {
      setGenerating(false);
    }
  }, [
    aadharNo,
    panNo,
    closeModal,
    aadharPanData,
    sale,
    secondaryUsers,
  ]);

  const handleUploadAgreement = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("agreement", selectedFile);
    
      
      formData.append("customer_sales_id", sale.customerSalesId);
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      await axiosInstance.post(
        `/customer/sales/agreement-notification?isFinal=${isFinalAgreementRadio}`,
        formData,
        config
      );


      
      toast.success("Successfully uploaded");
      setSelectedFile(null);
      if (onSaleUpdate) {
        onSaleUpdate();
      }
      fetchAgreements();
    } catch (error) {
      console.error("Error uploading agreement:", error);
      if (error.response && error.response.data) {
        const message =
          error.response.data.message || "Failed to upload the agreement";
        toast.error(message);
      } else {
        toast.error(error.message || "Failed to upload the agreement");
      }
    } finally {
      setUploading(false);
    }
  };

  const buttonStyle = (isDisabled) => ({
    backgroundColor: isDisabled ? "#ddd" : null,
    color: isDisabled ? "#999" : "white",
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.7 : 1,
    pointerEvents: isDisabled ? "none" : "auto",
  });

  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      zIndex: 1000,
      width: "100%",
      height: "100%",
    },
    content: {
      position: "fixed",
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "white",
      padding: "30px",
      borderRadius: "12px",
      maxHeight: "90vh",
      overflowY: "auto",
      minWidth: "400px",
      maxWidth: "95vw",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  };

  useEffect(() => {
    fetchAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale?.customerSalesId, user.userInfo?.token]);

  if (!sale) {
    return <div className="p-6">Sale data not available</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
   
      <h2 className="text-md font-semibold">Agreements</h2>
      <div className="">
        <p className="text-gray-700 my-1 text-sm">
          Uploaded Agreements:
          {fetchingAgreements ? (
            " Loading..."
          ) : agreementFiles && agreementFiles.length > 0 ? (
            <div className="flex flex-wrap gap-4 py-2">
              {agreementFiles.map((file, index) => (
                <div
                  key={index}
                  className="border border-gray-300 rounded p-4 flex items-center gap-2 w-60"
                >
                  <FileText className="text-gray-600" size={24} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-normal text-slate-800 truncate">
                        {file.name}
                      </p>
                      {file.kind && (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold border ${
                            file.kind === "Final"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-100 text-slate-600 border-slate-200"
                          }`}
                        >
                          {file.kind}
                        </span>
                      )}
                    </div>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-flamingo-500 text-xs "
                    >
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            " No Agreements Uploaded Yet "
          )}
        </p>
      </div>
      {hasPrivilege("canEdit") && (
      <div className="mb-6">
        <h3 className="text-md font-semibold">Generate Draft Agreement</h3>
        <GradientButton
          className={"w-fit my-2"}
          onClick={openModal}
          disabled={isHandover || isCancelled}
        >
          Generate Draft Agreement
        </GradientButton>
      </div>
      )}
      {hasPrivilege("canEdit") && (
      <div>
        <h3 className="text-ms font-semibold">Upload Agreement</h3>
        <div
          className={`border-dashed border-2 border-gray-300 my-2 rounded p-6 text-center flex flex-col items-center ${
            isHandover || isCancelled ? "opacity-50 cursor-not-allowed" : null
          }`}
          style={isHandover || isCancelled ? buttonStyle(true) : null}
        >
          <label
            htmlFor="file-upload"
            className="cursor-pointer hover:bg-gray-100 p-2 rounded"
          >
            {selectedFile ? (
              <p>Selected File : {selectedFile.name}</p>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-500 mx-auto p-2" />
                <p className="text-gray-500">Click to Upload PDF</p>
              </>
            )}
          </label>
          <input
            id="file-upload"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="hidden"
            disabled={isHandover || isCancelled}
          />
        </div>
        <div className="flex items-center justify-center mt-3">
          <label className="text-sm font-medium text-gray-700 mr-3">
            Is this the final Agreement?
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                value="yes"
                checked={isFinalAgreementRadio}
                onChange={handleFinalAgreementRadioChange}
                className="cursor-pointer"
                disabled={isHandover || isCancelled}
              />
              Yes
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                value="no"
                checked={!isFinalAgreementRadio}
                onChange={handleFinalAgreementRadioChange}
                className="cursor-pointer"
                disabled={isHandover || isCancelled}
              />
              No
            </label>
          </div>
        </div>
        {selectedFile && (
          <button
            onClick={handleUploadAgreement}
            disabled={uploading || isHandover || isCancelled}
            className={` text-white font-medium py-2 px-4 mt-2 rounded  ${
              uploading || isHandover || isCancelled
                ? "opacity-50 cursor-not-allowed bg-gray-300"
                : "bg-green-700 hover:bg-green-800"
            }`}
          >
            {uploading ? "Uploading..." : "Upload Agreement"}
          </button>
        )}
      </div>
      )}

      {/* Draft Agreement Modal */}
      {modalIsOpen && (
        <div style={modalStyle.overlay}>
          <div style={modalStyle.content}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-lg font-semibold text-slate-800">
                Draft Agreement Details
              </h2>
             
              <CloseButton onClick={closeModal} />
            </div>

            {/* Primary User Section */}
            <div className="mb-6 border-gray-200 border rounded-lg p-4 shadow-sm">
              <h3 className="text-md font-semibold  text-slate-700 mb-2">
                Primary User Details
              </h3>
              <div className="mb-4">
                <p
                  className="block text-slate-600 text-sm font-normal mb1
                "
                >
                  Name
                </p>
                <p className="font-medium text-slate-800">
                  {" "}
                  {sale?.user?.title} {sale?.user?.firstName}{" "}
                  {sale?.user?.lastName}
                </p>
              </div>
              <div className="mb-4">
                <label
                  htmlFor="aadharNo"
                  className="block text-slate-600 text-sm font-normal mb-1"
                >
                  Aadhar No
                </label>
                <input
                  type="text"
                  id="aadharNo"
                  value={aadharNo}
                  onChange={(e) => setAadharNo(e.target.value)}
                  placeholder="Enter Aadhar Number"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-sm"
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="panNo"
                  className="block text-slate-600 text-sm font-normal mb-1"
                >
                  Pan No
                </label>
                <input
                  type="text"
                  id="panNo"
                  value={panNo}
                  onChange={(e) => setPanNo(e.target.value)}
                  placeholder="Enter Pan Number"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-sm"
                />
              </div>
            </div>

            {/* Secondary Users Section */}
            {secondaryUsers && secondaryUsers.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-700">
                  Secondary Users Details
                </h3>
                <div className="space-y-6">
                  {secondaryUsers.map((userArray, index) => {
                    const user = userArray[0];
                    return (
                      <div
                        key={user.userId}
                        className="mb-4 p-4 border rounded border-gray-200 shadow-sm"
                      >
                        <p className="font-medium text-gray-800 mb-2 text-sm">
                          {user.title} {user.firstName} {user.lastName}
                        </p>
                        <div className="mb-3">
                          <label
                            htmlFor={`aadharNo-${user.userId}`}
                            className="block text-gray-600 text-xs font-medium mb-1"
                          >
                            Aadhar No
                          </label>
                          <input
                            type="text"
                            id={`aadharNo-${user.userId}`}
                            placeholder="Enter Aadhar Number"
                            className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                            value={aadharPanData[user.userId]?.aadharNo || ""}
                            onChange={(e) =>
                              handleInputChange(
                                user.userId,
                                "aadharNo",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`panNo-${user.userId}`}
                            className="block text-gray-600 text-xs font-medium mb-1"
                          >
                            Pan No
                          </label>
                          <input
                            type="text"
                            id={`panNo-${user.userId}`}
                            placeholder="Enter Pan Number"
                            className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                            value={aadharPanData[user.userId]?.panNo || ""}
                            onChange={(e) =>
                              handleInputChange(
                                user.userId,
                                "panNo",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="text-right mt-6 flex justify-end gap-4">
             
              <PrimaryButton
                onClick={handleGenerateDraft}
                disabled={generating || isHandover}
                className={""}
              >
                {generating ? "Generating..." : "Generate"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgreementsTab;
